import {
  CatalogExportPriceSettings,
  CatalogId,
  CatalogProductSearchTypeEnum,
  CatalogProductSortFieldEnum,
  CatalogSearchTypeEnum,
  CatalogSortFieldEnum,
  ConfigurationOptionSearchResult,
  ExportResult,
  JobSearchResult,
  JobSortSettings,
  SortDirectionEnum,
  CatalogItem,
  FilterSettings,
  CatalogProductUpdateItem,
  CatalogProductDeleteItem,
  CatalogActionResult,
  CatalogProductItem,
  JobAction,
  TreeTypeEnum,
  CatalogProductQuantityItem,
} from '@/common/services/swagger/index.defs';

import { Instance } from '../Instance';
import { DisplayMode, IRouteData } from './IRouteData';
import _cloneDeep from 'lodash/cloneDeep';
import _omit from 'lodash/omit';
import { ICommand } from '@/common/api/runtime/ICommand';
import { downloadFile } from '@/common/helpers/downloadFile';
import { INotification, NotificationType } from '@/common/api/runtime/INotification';
import {
  CatalogAnyActionData,
  CatalogCopyActionData,
  CatalogDeleteActionData,
  CatalogEditActionData,
  CatalogExportActionData,
  CatalogHistoryActionData,
  CatalogImportActionData,
  CatalogNewActionData,
  CatalogProductCopyActionData,
  CatalogProductDeleteActionData,
  CatalogProductEditActionData,
} from './CatalogActionData';
import { CatalogAction } from './CatalogAction';

const executePlaceholder = (name: string) => {
  alert(`Command "${name}" should be handled by application`);
  return Promise.resolve();
};

export abstract class Command<TArgs> implements ICommand<Instance> {
  isHandled = false;
  constructor(public args: TArgs) {}
  abstract execute: (instance: Instance) => Promise<unknown>;
  abstract name: string;
}

export class DetailsCommand extends Command<{ productId: string; catalog?: CatalogItem }> {
  name = 'details';

  constructor(args: { productId: string; catalogId?: CatalogId }) {
    super(args);
  }

  public execute = async (): Promise<unknown> => {
    return executePlaceholder(this.name);
  };
}

export class SearchCommand extends Command<{
  searchText: string;
  searchType: CatalogSearchTypeEnum;
}> {
  name = 'catalogs-filter-search';

  public execute = async (instance: Instance): Promise<unknown> => {
    const routeData = {
      view: 'catalogs',
      catalogs: {
        searchType: this.args.searchType,
        searchText: this.args.searchText,
        pagination: { page: 1 },
      },
    } as IRouteData;

    return await instance.router?.setRoute(routeData);
  };
}

export class SearchProductsCommand extends Command<{
  searchText: string;
  searchType: CatalogProductSearchTypeEnum;
  resetCategories?: boolean;
  resetFilters?: boolean;
}> {
  name = 'products-filter-search';

  constructor(args: {
    searchText: string;
    searchType: CatalogProductSearchTypeEnum;
    resetCategories?: boolean;
    resetFilters?: boolean;
  }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    const routeData = {
      view: 'products',
      products: {
        searchType: this.args.searchType,
        searchText: this.args.searchText,
        pagination: { page: 1 },
      },
    } as IRouteData;

    if (this.args.resetCategories) {
      routeData.products.cid = undefined;
      routeData.products.treeType = undefined;
    }

    if (this.args.resetFilters) {
      routeData.products.filters = undefined;
    }

    return await instance.router?.setRoute(routeData);
  };
}

const reloadCatalogs = async (instance: Instance) =>
  instance.router?.setRoute({
    ...instance.router?.routeData,
    view: 'catalogs',
    catalogs: {
      ...instance.router?.routeData.catalogs,
      //sortField: CatalogSortFieldEnum.Code,
      searchType: undefined,
      searchText: undefined,
      pagination: { page: 1 },
    },
  } as IRouteData);

const reloadProducts = async (instance: Instance) =>
  instance.router?.setRoute({
    ...instance.router?.routeData,
    view: 'products',
    products: {
      ...instance.router?.routeData.products,
      searchText: undefined,
      cid: instance.router?.routeData.products.cid,
      filters: undefined,
      catalog: undefined,
    },
  } as IRouteData);

export class CatalogsCommand extends Command<void> {
  name = 'catalogs';

  public execute = async (instance: Instance): Promise<unknown> => reloadCatalogs(instance);
}

export class ProductsCommand extends Command<void> {
  name = 'products';

  public execute = async (instance: Instance): Promise<unknown> => reloadProducts(instance);
}

export class CatalogProductsCommand extends Command<{ catalogId: CatalogId }> {
  name = 'catalog-products';

  constructor(args: { catalogId: CatalogId }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    return await instance.router?.setRoute({
      view: 'products',
      products: {
        searchText: undefined,
        cid: undefined,
        filters: undefined,
        catalog: this.args.catalogId,
        pagination: {
          page: 1,
          pageSize: instance.getRouteData().products.pagination.pageSize,
        },
      },
    } as IRouteData);
  };
}

export class ClassificationCommand extends Command<{
  cid: string;
  treeType?: TreeTypeEnum;
  clearSearch: boolean;
}> {
  name = 'products-filter-classification';

  constructor(args: { cid: string; treeType?: TreeTypeEnum; clearSearch: boolean }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    const route = instance.getRouteData();

    route.view = 'products';

    route.products.pagination.page = 1;
    route.products.cid = this.args.cid;
    route.products.treeType = this.args.treeType;

    if (this.args.clearSearch) {
      route.products.searchText = undefined;
    }

    return await instance.router?.setRoute(route);
  };
}

export class PreviewCommand extends Command<{ productId: string }> {
  name = 'product-preview';

  constructor(args: { productId: string }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    return await instance.httpService?.preview(this.args.productId);
  };
}

export class ApplyFiltersCommand extends Command<{
  filters: FilterSettings[];
}> {
  name = 'products-filter-parametric-apply';

  constructor(args: { filters: FilterSettings[] }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    return await instance.router?.setRoute({
      view: 'products',
      products: {
        filters: this.args.filters,
        pagination: {
          page: 1,
          pageSize: instance.router.routeData.products.pagination.pageSize,
        },
      },
    } as IRouteData);
  };
}

export class ExcludeFilterCommand extends Command<{
  attributeCode: string;
  value: string;
}> {
  name = 'products-filter-parametric-remove';

  constructor(args: { attributeCode: string; value: string }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    const filters = instance.router.routeData?.products.filters
      ?.map(
        ({ attributeCode, attributeValues }): FilterSettings => ({
          attributeCode,
          attributeValues: attributeValues?.filter((val) => val !== this.args.value),
        }),
      )
      .filter(({ attributeValues }) => attributeValues?.length);

    return await instance.router?.setRoute({
      products: {
        filters,
        pagination: {
          page: 1,
          pageSize: instance.router.routeData.products.pagination.pageSize,
        },
      },
    } as IRouteData);
  };
}

export class GetFilterValuesCommand extends Command<{ attributeCodes: string[] }> {
  name = 'products-get-filter-values';

  constructor(args: { attributeCodes: string[] }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    const routeDate = instance.getRouteData();

    return await instance.httpService?.filterValues(
      this.args.attributeCodes,
      routeDate.products.catalog,
    );
  };
}

export class ProductsDisplayModeCommand extends Command<{ mode: DisplayMode }> {
  name = 'products-display-mode';

  constructor(args: { mode: DisplayMode }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    return await instance.router?.setRoute({
      products: { displayMode: this.args.mode },
    } as IRouteData);
  };
}

export class ProductsPageSizeCommand extends Command<{ pageSize: number }> {
  name = 'products-page-size';

  constructor(args: { pageSize: number }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    return await instance.router?.setRoute({
      products: {
        pagination: {
          pageSize: this.args.pageSize,
          page: 1,
        },
      },
    } as IRouteData);
  };
}

export class CatalogsPageSizeCommand extends Command<{ pageSize: number }> {
  name = 'catalogs-page-size';

  constructor(args: { pageSize: number }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    return await instance.router?.setRoute({
      catalogs: {
        pagination: {
          pageSize: this.args.pageSize,
          page: 1,
        },
      },
    } as IRouteData);
  };
}

export class ProductsSortByCommand extends Command<{ sortField: CatalogProductSortFieldEnum }> {
  name = 'products-sort-by';

  constructor(args: { sortField: CatalogProductSortFieldEnum }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    return await instance.router?.setRoute({
      products: {
        sortField: this.args.sortField,
        sortDirection:
          this.args.sortField == CatalogProductSortFieldEnum.LastImport
            ? SortDirectionEnum.Descending
            : SortDirectionEnum.Ascending,
      },
    } as IRouteData);
  };
}

export class ProductsPaginateCommand extends Command<{ page: number }> {
  name = 'products-paginate';

  constructor(args: { page: number }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    return await instance.router?.setRoute({
      products: {
        pagination: {
          page: this.args.page,
        },
      },
    } as IRouteData);
  };
}

export class CatalogsPaginateCommand extends Command<{ page: number }> {
  name = 'catalogs-paginate';

  constructor(args: { page: number }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    return await instance.router?.setRoute({
      catalogs: {
        pagination: {
          page: this.args.page,
        },
      },
    } as IRouteData);
  };
}
export class UpdateProductAliasesCommand extends Command<any> {
  name = 'catalog-update-product-aliases';

  constructor(args: any) {
    super(args);
  }

  public execute = async (): Promise<any> => {
    /** @todo: action */
    // eslint-disable-next-line
    console.log(this.args);
  };
}

export class UpdateProductCommand extends Command<{
  product: CatalogProductUpdateItem;
  catalogId: CatalogId;
}> {
  name = 'catalog-update-product';

  public execute = async (instance: Instance): Promise<any> => {
    const result: CatalogActionResult | undefined =
      await instance.httpService?.updateCatalogProduct(this.args.product, this.args.catalogId);
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    // Patching store
    instance.store.data.products?.items?.forEach((catalogProduct: CatalogProductItem) => {
      if (catalogProduct.productId === this.args.product.productId) {
        catalogProduct.catalogs?.forEach((catalog) => {
          if (catalog.catalogId?.code === this.args.catalogId.code) {
            catalog.quantities?.forEach((quantity: CatalogProductQuantityItem) => {
              if (quantity.unitOfMeasure === this.args.product.unitOfMeasure) {
                quantity.value = this.args.product.quantity;
              }
            });
          }
        });
      }
    });
  };
}

export interface CopyProductCommandArgs {
  productId: string;
  unitOfMeasure: string;
  sourceCatalogId: CatalogId;
  targetCatalogId: CatalogId;
}
export class CopyProductCommand extends Command<CopyProductCommandArgs> {
  name = 'catalog-copy-product';

  public execute = async (instance: Instance): Promise<any> => {
    const result: CatalogActionResult | undefined = await instance.httpService?.copyCatalogProduct(
      this.args.productId,
      this.args.unitOfMeasure,
      this.args.sourceCatalogId,
      this.args.targetCatalogId,
    );
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    return reloadProducts(instance);
  };
}

export class CatalogsSortByCommand extends Command<{ sortField: CatalogSortFieldEnum }> {
  name = 'catalogs-sort-by';

  constructor(args: { sortField: CatalogSortFieldEnum }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    const newRoute = {
      catalogs: {
        sortField: this.args.sortField,
        sortDirection:
          this.args.sortField == CatalogSortFieldEnum.LastModified
            ? SortDirectionEnum.Descending
            : SortDirectionEnum.Ascending,
      },
    } as IRouteData;

    return await instance.router?.setRoute(newRoute);
  };
}

export class CatalogsFilterByDateCommand extends Command<{ days?: number | string | undefined }> {
  name = 'catalogs-filter-by-date';

  constructor(args: { days?: number | string | undefined }) {
    super(args);
  }

  public execute = async (instance: Instance): Promise<unknown> => {
    const newRoute = {
      catalogs: {
        createdWithinLastDays: this.args.days,
      },
    } as IRouteData;

    return await instance.router?.setRoute(newRoute);
  };
}

export class DownloadJobHistoryCommand extends Command<{ sortSettings?: JobSortSettings }> {
  name = 'catalogs-job-history-download';

  public execute = async (instance: Instance): Promise<JobSearchResult | undefined> => {
    instance.store.data.isLoadingData = true;
    const result: JobSearchResult | undefined = await instance.httpService?.downloadJobHistory(
      this.args.sortSettings,
    );
    instance.store.data.isLoadingData = false;
    return result;
  };
}

export class ConfiguredProductsCommand extends Command<{
  configuredProductId: string;
  productId: string;
  unitOfMeasure: string;
  catalogId: CatalogId;
}> {
  name = 'catalog-configured-products';

  public execute = async (
    instance: Instance,
  ): Promise<ConfigurationOptionSearchResult | undefined> => {
    return instance.httpService?.getConfigurationOption(
      this.args.configuredProductId,
      this.args.productId,
      this.args.unitOfMeasure,
      this.args.catalogId,
    );
  };
}

export type DownloadJobCommandArgs = Pick<JobAction, 'catalogId' | 'jobStatus' | 'jobType'>;
export class DownloadJobCommand extends Command<DownloadJobCommandArgs> {
  name = 'catalogs-job-download';
  public execute = async (instance: Instance): Promise<any | undefined> => {
    instance.store.data.isLoadingData = true;
    const result: ExportResult | undefined = await instance.httpService?.exportJobHistory(
      this.args,
    );
    instance.store.data.isLoadingData = false;
    if (!result || !result.guid) {
      throw new Error('Job download error');
    }
    const filename = `Export Job ${this.args.catalogId?.code}.pdf`;
    const url = `${instance.getOptions().baseUrl}/v1/Catalogs/Jobs/Download?guid=${result.guid}`;
    await downloadFile(filename, url);
    instance.eventBus.emit('job-download-done', url);
    return;
  };
}

export class CatalogImportCommand extends Command<{
  catalogId: CatalogId;
  file: string;
  fileName: string;
}> {
  name = 'catalog-import';

  public execute = async (instance: Instance): Promise<any> => {
    instance.store.data.isLoadingData = true;
    const result: CatalogActionResult | undefined = await instance.httpService?.importCatalog(
      this.args.catalogId,
      this.args.file,
      this.args.fileName,
    );
    instance.store.data.isLoadingData = false;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    return reloadCatalogs(instance);
  };
}
export interface CatalogExportCommandArgs {
  catalogId: CatalogId;
  priceSettings?: CatalogExportPriceSettings;
}
export class CatalogExportCommand extends Command<CatalogExportCommandArgs> {
  name = 'catalog-export';
  public execute = async (instance: Instance): Promise<boolean> => {
    instance.store.data.isLoadingData = true;
    const result: (ExportResult & Record<string, string>) | undefined = <any>(
      await instance.httpService?.exportCatalog(this.args.catalogId, this.args.priceSettings)
    );
    instance.store.data.isLoadingData = false;
    if (!result) {
      throw new Error('Catalog export error');
    }

    if (result && result.guid && result.type === 'Download') {
      const filename = `Export ${this.args.catalogId.code}.pdf`;
      const url = `${instance.getOptions().baseUrl}/v1/Catalogs/Download?guid=${result.guid}`;
      await downloadFile(filename, url);
      instance.eventBus.emit('catalog-export-done', url);

      return false;
    }

    return true;
  };
}

export class CatalogImportAliasesCommand extends Command<{
  file: string;
  fileName: string;
}> {
  name = 'catalog-import-aliases';
  public execute = async (instance: Instance): Promise<any> => {
    instance.store.data.isLoadingData = true;
    const result: CatalogActionResult | undefined =
      await instance.httpService?.importCatalogAliases(this.args.file, this.args.fileName);
    instance.store.data.isLoadingData = false;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    return reloadCatalogs(instance);
  };
}
export class CatalogExportAliasesCommand extends Command<void> {
  name = 'catalog-export-aliases';
  public execute = async (instance: Instance): Promise<boolean> => {
    instance.store.data.isLoadingData = true;
    const result: (ExportResult & Record<string, string>) | undefined = <any>(
      await instance.httpService?.exportCatalogAliases()
    );
    instance.store.data.isLoadingData = false;
    if (!result) {
      throw new Error('Catalog aliases export error');
    }
    if (result && result.guid) {
      const filename = `Export aliases ${result.guid}.pdf`;
      const url = `${instance.getOptions().baseUrl}/v1/Catalogs/Aliases/Download?guid=${
        result.guid
      }`;
      await downloadFile(filename, url);
      instance.eventBus.emit('catalog-export-aliases-done', url);
      return false;
    }
    return true;
  };
}

export class CatalogCreateNewCommand extends Command<{
  catalogId: CatalogId;
  description: string;
}> {
  name = 'catalog-create-new';
  public execute = async (instance: Instance): Promise<void> => {
    instance.store.data.isLoadingData = true;
    const result: CatalogActionResult | undefined = await instance.httpService?.addCatalog(
      this.args.catalogId,
      this.args.description,
    );
    instance.store.data.isLoadingData = false;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    return reloadCatalogs(instance);
  };
}

export class CatalogUpdateCommand extends Command<{ catalogId: CatalogId; description: string }> {
  name = 'catalog-update';
  public execute = async (instance: Instance): Promise<void> => {
    instance.store.data.isLoadingData = true;
    const result: CatalogActionResult | undefined = await instance.httpService?.updateCatalog(
      this.args.catalogId,
      this.args.description,
    );
    instance.store.data.isLoadingData = false;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    return reloadCatalogs(instance);
  };
}

export interface CatalogCopyCommandArgs {
  sourceCatalogId: CatalogId;
  targetCatalogId: CatalogId;
}
export class CatalogCopyCommand extends Command<CatalogCopyCommandArgs> {
  name = 'catalog-copy';
  public execute = async (instance: Instance): Promise<void | never> => {
    instance.store.data.isLoadingData = true;
    const result: CatalogActionResult | undefined = await instance.httpService?.copyCatalog(
      this.args.sourceCatalogId,
      this.args.targetCatalogId,
    );
    instance.store.data.isLoadingData = false;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    return reloadCatalogs(instance);
  };
}
export class CatalogDeleteCommand extends Command<{ catalogId: CatalogId }> {
  name = 'catalog-delete';
  public execute = async (instance: Instance): Promise<void> => {
    instance.store.data.isLoadingData = true;
    const result: CatalogActionResult | undefined = await instance.httpService?.deleteCatalog(
      this.args.catalogId,
    );
    instance.store.data.isLoadingData = false;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    return reloadCatalogs(instance);
  };
}
export class CatalogMakeDefaultCommand extends Command<{ catalogId: CatalogId }> {
  name = 'catalog-set-default';
  public execute = async (instance: Instance): Promise<void> => {
    instance.store.data.isLoadingData = true;
    const result: CatalogActionResult | undefined =
      await instance.httpService?.updateDefaultCatalog(this.args.catalogId, true);
    instance.store.data.isLoadingData = false;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    return reloadCatalogs(instance);
  };
}
export class CatalogUnMakeDefaultCommand extends Command<{ catalogId: CatalogId }> {
  name = 'catalog-remove-default';
  public execute = async (instance: Instance): Promise<void> => {
    instance.store.data.isLoadingData = true;
    const result: CatalogActionResult | undefined =
      await instance.httpService?.updateDefaultCatalog(this.args.catalogId, false);
    instance.store.data.isLoadingData = false;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    return reloadCatalogs(instance);
  };
}
export class CatalogProductDeleteCommand extends Command<{
  products: CatalogProductDeleteItem[];
  catalogId: CatalogId;
}> {
  name = 'catalog-product-delete';
  public execute = async (instance: Instance): Promise<void> => {
    instance.store.data.isLoadingData = true;
    const result: CatalogActionResult | undefined =
      await instance.httpService?.deleteCatalogProduct(this.args.products, this.args.catalogId);
    instance.store.data.isLoadingData = false;
    if (!result || !result.success) {
      throw new Error(result?.message || 'Unknown error');
    }
    // Filter products to remove deleted
    const newProductsItems: CatalogProductItem[] =
      instance.store.data.products?.items?.filter((product: CatalogProductItem) => {
        const productId: string = product.productId;
        const catalog = product.catalogs?.find(
          (item) => item.catalogId?.code === this.args.catalogId.code,
        );
        if (!catalog) {
          return true;
        }
        const uoms: string[] | undefined = catalog.quantities?.map((item) => item.unitOfMeasure);
        if (!uoms) {
          return true;
        }
        return !this.args.products.find(
          (productsForDelete: CatalogProductDeleteItem) =>
            productsForDelete.productId === productId &&
            uoms.includes(productsForDelete.unitOfMeasure),
        );
      }) || [];
    // Patch products store
    Object.assign(instance.store.data, {
      products: {
        items: newProductsItems,
        totalCount: newProductsItems.length,
      },
    });
    const newItems: CatalogItem[] = instance.store.data.catalogs?.items || [];
    const catalogForUpdate: CatalogItem | undefined = newItems.find(
      (catalog: CatalogItem) => catalog.catalogId?.code === this.args.catalogId.code,
    );
    if (catalogForUpdate) {
      let newProductsTotalCount = catalogForUpdate.productsTotalCount - this.args.products.length;
      if (newProductsTotalCount <= 0) {
        newProductsTotalCount = 0;
      }
      catalogForUpdate.productsTotalCount = newProductsTotalCount;
    }
    Object.assign(instance.store.data, {
      catalogs: {
        items: newItems,
        totalCount: newItems.length,
      },
    });
  };
}

export class AddNotificationCommand extends Command<INotification> {
  name = 'notification-show';
  public execute = async (instance: Instance): Promise<void> => {
    let showDelay = 0;

    if (instance.store.notifications.length) {
      instance.store.notifications = [];
      showDelay = 1000;
    }

    if (!this.args.dismissAfter) {
      if (this.args.type != NotificationType.danger) {
        this.args.dismissAfter = 6000;
        instance.store.notifications = [];
      }
    }

    if (this.args.dismissAfter) {
      setTimeout(() => {
        const index = instance.store.notifications.indexOf(this.args);
        if (index !== -1) {
          instance.store.notifications.splice(index, 1);
        }
      }, this.args.dismissAfter);
    }
    setTimeout(() => {
      return instance.store.notifications.push(this.args);
    }, showDelay);
  };
}

export class DeleteNotificationCommand extends Command<INotification> {
  name = 'notification-hide';
  public execute = async (instance: Instance): Promise<void> => {
    const index = instance.store.notifications.indexOf(this.args);
    if (index !== -1) {
      instance.store.notifications.splice(index, 1);
    }
  };
}

export class CatalogActionCompleted extends Command<void> {
  name = 'catalog-action-completed';
  public execute = async (instance: Instance) =>
    Object.assign(instance.store.data.actions, {
      current: null,
      data: null,
    });
}

const setCatalogAction = async (
  instance: Instance,
  current: CatalogAction,
  data: CatalogAnyActionData,
) =>
  Object.assign(instance.store.data.actions, {
    current,
    data,
  });

export class CatalogEditActionCommand extends Command<CatalogEditActionData> {
  name = 'catalog-edit-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.edit, this.args);
}

export class CatalogCopyActionCommand extends Command<CatalogCopyActionData> {
  name = 'catalog-copy-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.copy, this.args);
}

export class CatalogImportActionCommand extends Command<CatalogImportActionData> {
  name = 'catalog-import-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.import, this.args);
}

export class CatalogExportActionCommand extends Command<CatalogExportActionData> {
  name = 'catalog-export-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.export, this.args);
}

export class CatalogDeleteActionCommand extends Command<CatalogDeleteActionData> {
  name = 'catalog-delete-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.delete, this.args);
}

export class CatalogHistoryActionCommand extends Command<CatalogHistoryActionData> {
  name = 'catalog-history-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.history, this.args);
}

export class CatalogNewActionCommand extends Command<CatalogNewActionData> {
  name = 'catalog-new-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.createNew, this.args);
}
export class CatalogProductEditActionCommand extends Command<CatalogProductEditActionData> {
  name = 'catalog-product-edit-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.productEdit, this.args);
}
export class CatalogProductCopyActionCommand extends Command<CatalogProductCopyActionData> {
  name = 'catalog-product-copy-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.productCopy, this.args);
}
export class CatalogProductDeleteActionCommand extends Command<CatalogProductDeleteActionData> {
  name = 'catalog-product-delete-action';
  public execute = async (instance: Instance) =>
    setCatalogAction(instance, CatalogAction.productDelete, this.args);
}

export class GetAllCatalogsCommand extends Command<void> {
  name = 'catalogs-fetch';
  public execute = async (instance: Instance): Promise<CatalogItem[]> => {
    instance.store.data.isLoadingData = true;
    const result = await instance.httpService?.getAllCatalogs();
    instance.store.data.isLoadingData = false;
    if (!result || !result.items) {
      return [];
    }
    return result.items;
  };
}

export class CommandExecutor {
  constructor(private instance: Instance) {}

  public async execute(command: ICommand<Instance>): Promise<unknown> {
    const commandInterceptor = this.instance.store.options.commandInterceptor;

    if (commandInterceptor) {
      try {
        // we don't want to expose execute function nor let external app to modify args
        // we clone the command exposed externally
        const commandExternal = _cloneDeep(_omit(command, ['execute']));

        await commandInterceptor(commandExternal as ICommand<Instance>);
        if (commandExternal.isHandled) {
          return Promise.reject();
        }

        return command?.execute(this.instance);
      } catch (e) {
        this.instance.logger.log('Command cancelled', command);
      }
    } else {
      return command?.execute(this.instance);
    }
  }
}
